/**
 * Derives the accent custom properties the dock uses.
 *
 * The host app configures one hex value; hover, tint and contrast are computed
 * from it here rather than being three more config keys nobody wants to tune.
 *
 * Note these are deliberately NOT the shadcn `--accent` token. In this widget's
 * palette `--accent` is a light neutral used by every `hover:bg-accent` in the
 * existing components — overriding it with a brand colour would repaint every
 * hover state in the widget.
 */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const FALLBACK = '#1f6f6a';

function parseHex(hex: string): Rgb {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  const value = match ? match[1] : FALLBACK.slice(1);

  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

/** Relative luminance, per WCAG, for picking readable text over the accent. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (raw: number) => {
    const c = raw / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function shift({ r, g, b }: Rgb, amount: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `rgb(${clamp(r + amount)}, ${clamp(g + amount)}, ${clamp(b + amount)})`;
}

export interface AccentVars {
  '--assistant-accent': string;
  '--assistant-accent-hover': string;
  '--assistant-accent-soft': string;
  '--assistant-accent-contrast': string;
}

export function accentVars(hex: string): AccentVars {
  const rgb = parseHex(hex);
  const light = luminance(rgb) > 0.45;

  return {
    '--assistant-accent': `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
    // A light accent darkens on hover, a dark one lightens — either way the
    // hover state stays visible instead of disappearing at the extremes.
    '--assistant-accent-hover': shift(rgb, light ? -22 : 24),
    '--assistant-accent-soft': `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.11)`,
    '--assistant-accent-contrast': light ? '#0a0c0c' : '#ffffff',
  };
}
